/**
 * Shape of the validation-failure body returned by the Prism backend
 * (`ValidationHelper.CreateValidationErrorResponse`). A validation 400 always
 * carries a flat array of human-readable, display-safe strings under `errors`.
 *
 * @see PRM-579 — backend now returns a true 400 (previously a masked 200) for
 * validation failures.
 */
export interface ValidationErrorResponse {
  errors: string[];
}

/**
 * The RTK Query / fetch error shape we branch on. RTK Query surfaces a rejected
 * mutation as `{ status, data }`, where `status` is the HTTP status code and
 * `data` is the parsed JSON body.
 */
interface StatusBearingError {
  status?: number | string;
  data?: unknown;
  originalStatus?: number;
}

/**
 * Extracts backend validation messages from a failed request, keyed off the
 * HTTP **status code** (not the presence of a body) per the PRM-579 contract.
 *
 * Returns the `errors[]` array only when the response is a `400 Bad Request`
 * carrying the `{ errors: string[] }` shape. For any other status (401/403/500/
 * network) it returns `null` so the caller can fall back to generic handling.
 *
 * @param error - The error thrown/returned by an RTK Query mutation (e.g. from
 *   `.unwrap()`), or any object exposing `{ status, data }`.
 * @returns A non-empty array of user-facing messages when the error is a
 *   validation 400, otherwise `null`.
 */
export function extractBackendValidationErrors(error: unknown): string[] | null {
  if (!error || typeof error !== 'object') return null;

  const err = error as StatusBearingError;
  const status = err.originalStatus ?? err.status;

  // Only the 400 validation shape is parsed here. Normalize to number since RTK
  // can surface `status` as a string in some transports.
  if (status !== 400 && status !== '400') return null;

  const body = err.data;
  if (!body || typeof body !== 'object') return null;

  const errors = (body as { errors?: unknown }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const cleaned = (errors as unknown[]).filter(
      (m): m is string => typeof m === 'string' && m.trim().length > 0,
    );
    return cleaned.length > 0 ? cleaned : null;
  }

  return null;
}

